import { useFlushOnLeave } from '@src/hooks/useFlushOnLeave';
import {
  useDashboard,
  useUserStoryUploadFlow,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';

export const clientVideoAnalysisMessage = (title: string): string =>
  `${title} · Analyzing Video`;

type ToastProps = { title: string; progress: number };

/**
 * One `pending` toast with a progress bar for a running analysis. Renders
 * nothing itself — it drives the shared toast queue: shown on mount, updated
 * in place, taken down on unmount (the run ended). If the user closes it, it
 * stays closed; the analysis carries on.
 */
const ClientVideoAnalysisToast = observer(({ title, progress }: ToastProps) => {
  const videoCreator = useVideoCreatorStore();
  const toastIdRef = useRef<number | null>(null);
  const stoppedRef = useRef(false);
  const message = clientVideoAnalysisMessage(title);
  // Read during render so a dismissal re-runs the effect below.
  const queue = videoCreator.toasts;

  useEffect(() => {
    if (stoppedRef.current) return;
    if (toastIdRef.current === null) {
      toastIdRef.current = videoCreator.showToast({
        state: 'pending',
        message,
        progress,
      });
      return;
    }
    const id = toastIdRef.current;
    const current = videoCreator.toasts.find(toast => toast.id === id);
    if (!current) {
      toastIdRef.current = null;
      stoppedRef.current = true;
      return;
    }
    if (current.message !== message || current.progress !== progress) {
      videoCreator.updateToast(id, { state: 'pending', message, progress });
    }
  }, [videoCreator, message, progress, queue]);

  // Clearing the ref matters under StrictMode's mount → cleanup → mount.
  useEffect(
    () => () => {
      if (toastIdRef.current !== null) {
        videoCreator.dismissToast(toastIdRef.current);
      }
      toastIdRef.current = null;
    },
    [videoCreator],
  );

  return null;
});

/**
 * PLAT-12104 / PLAT-12105: a progress toast for each in-browser scene/face
 * detection run, on every page, and the browser's leave-site prompt while any
 * run is going — closing the tab kills the run (the server's detection then
 * covers it, just later).
 */
const ClientVideoAnalysisToaster = observer(() => {
  const uploadFlowStore = useUserStoryUploadFlow();
  const dashboardStore = useDashboard();
  const runs = Object.entries(uploadFlowStore.clientVideoAnalysisRecord);

  useFlushOnLeave(
    () => {},
    () => Object.keys(uploadFlowStore.clientVideoAnalysisRecord).length > 0,
  );

  return (
    <>
      {runs.map(([storyId, progress]) => (
        <ClientVideoAnalysisToast
          key={storyId}
          title={
            dashboardStore.allStories.find(s => s.id === storyId)?.title ||
            'Your video'
          }
          progress={progress}
        />
      ))}
    </>
  );
});

export default ClientVideoAnalysisToaster;
